import { FC, useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SidebarHeader from 'src/components/Headers/SideBarHeader';
import { handleLogout } from 'src/utils/auth.utils';
import { getEncryptedLocalStorageData } from 'src/utils/dataStorageManagement.utils';
import { dataStorageKeywords, userRoles } from 'src/utils/properties.utils';
import { fetchHighestRole, createLink } from 'src/utils/sidebar.utils';
import {
  GRNIcon,
  DiscountIcon,
  ProductionMappingIcon,
  CustomerIcon,
  OrderIcon,
  ProductionIcon,
} from 'src/components/StyledComponents/CustomStyledSVG';
import Scrollbar from 'src/components/StyledComponents/StyledScrollbar';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  SidebarContainer,
  SidebarScrollableContent,
  SidebarContentPadding,
  SidebarLink,
  SidebarLinkText,
  SidebarIcon,
  SidebarFooter,
  SidebarDivider,
  SidebarDropdownToggle,
  SidebarDropdownToggleText,
  SidebarDropdownArrow,
  SidebarDropdownContent,
  SidebarCloseIcon,
} from 'src/components/StyledComponents/StyledSidebar';

interface SidebarProps {
  mobileOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: FC<SidebarProps> = ({ mobileOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showSnackbar } = StyledSnackbar();
  const [activeItem, setActiveItem] = useState<string>(location.pathname);
  const [highestRole, setHighestRole] = useState<string | null>(null);

  const [adminDropdownOpen, setAdminDropdownOpen] = useState<boolean>(false);
  const [reportsDropdownOpen, setReportsDropdownOpen] =
    useState<boolean>(false);

  // Callback ref: scrolls the active sidebar link into view on mount
  const activeLinkRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      node.scrollIntoView({ behavior: 'auto', block: 'nearest' });
    }
  }, []);

  const isAdminOrOwner =
    highestRole === userRoles.ADMIN || highestRole === userRoles.OWNER;
  const isExecutive = highestRole === userRoles.EXECUTIVE;

  useEffect(() => {
    const loadHighestRole = async () => {
      const role = await fetchHighestRole(
        getEncryptedLocalStorageData,
        dataStorageKeywords.HIGHEST_ROLE
      );
      setHighestRole(role);
    };

    loadHighestRole();
  }, []);

  useEffect(() => {
    setActiveItem(location.pathname);
  }, [location.pathname]);

  const handleClick = (path: string) => {
    setActiveItem(path);
    if (onClose) onClose();
  };

  return (
    <SidebarContainer mobileOpen={mobileOpen}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <SidebarHeader />
        {/* Refactored inline sx from lines 107-113 to SidebarCloseIcon styled component */}
        {mobileOpen && (
          <SidebarCloseIcon
            component="i"
            className="bx bx-x"
            onClick={onClose}
          />
        )}
      </Box>

      <SidebarScrollableContent>
        <Scrollbar>
          <SidebarContentPadding>
            <SidebarLink
              ref={
                activeItem === createLink('dashboard')
                  ? activeLinkRef
                  : undefined
              }
              component={Link}
              to={createLink('dashboard')}
              onClick={() => handleClick(createLink('dashboard'))}
              isActive={activeItem === createLink('dashboard')}
            >
              <SidebarIcon
                component="i"
                className="bx bxs-dashboard"
                isActive={activeItem === createLink('dashboard')}
              />
              <SidebarLinkText
                isActive={activeItem === createLink('dashboard')}
              >
                Dashboard
              </SidebarLinkText>
            </SidebarLink>

            <SidebarLink
              ref={
                activeItem === createLink('order-management')
                  ? activeLinkRef
                  : undefined
              }
              component={Link}
              to={createLink('order-management')}
              onClick={() => handleClick(createLink('order-management'))}
              isActive={activeItem === createLink('order-management')}
            >
              <SidebarIcon
                isActive={activeItem === createLink('order-management')}
              >
                <OrderIcon
                  color={
                    activeItem === createLink('order-management')
                      ? 'var(--bg-color)'
                      : 'var(--subtext)'
                  }
                  size="16px"
                />
              </SidebarIcon>
              <SidebarLinkText
                isActive={activeItem === createLink('order-management')}
              >
                Orders
              </SidebarLinkText>
            </SidebarLink>

            <SidebarLink
              ref={
                activeItem === createLink('voucher-management')
                  ? activeLinkRef
                  : undefined
              }
              component={Link}
              to={createLink('voucher-management')}
              onClick={() => handleClick(createLink('voucher-management'))}
              isActive={activeItem === createLink('voucher-management')}
            >
              <SidebarIcon
                component="i"
                className="bx bxs-coupon 1"
                isActive={activeItem === createLink('voucher-management')}
              />
              <SidebarLinkText
                isActive={activeItem === createLink('voucher-management')}
              >
                Vouchers
              </SidebarLinkText>
            </SidebarLink>

            <SidebarLink
              ref={
                activeItem === createLink('purchase-order-management')
                  ? activeLinkRef
                  : undefined
              }
              component={Link}
              to={createLink('purchase-order-management')}
              onClick={() =>
                handleClick(createLink('purchase-order-management'))
              }
              isActive={activeItem === createLink('purchase-order-management')}
            >
              <SidebarIcon
                component="i"
                className="bx bxs-purchase-tag-alt"
                isActive={
                  activeItem === createLink('purchase-order-management')
                }
              />
              <SidebarLinkText
                isActive={
                  activeItem === createLink('purchase-order-management')
                }
              >
                Purchase Orders
              </SidebarLinkText>
            </SidebarLink>

            <SidebarLink
              ref={
                activeItem === createLink('grn-management')
                  ? activeLinkRef
                  : undefined
              }
              component={Link}
              to={createLink('grn-management')}
              onClick={() => handleClick(createLink('grn-management'))}
              isActive={activeItem === createLink('grn-management')}
            >
              <SidebarIcon
                isActive={activeItem === createLink('grn-management')}
              >
                <GRNIcon
                  color={
                    activeItem === createLink('grn-management')
                      ? 'var(--bg-color)'
                      : 'var(--subtext)'
                  }
                  size="16px"
                />
              </SidebarIcon>
              <SidebarLinkText
                isActive={activeItem === createLink('grn-management')}
              >
                GRN
              </SidebarLinkText>
            </SidebarLink>

            <SidebarLink
              ref={
                activeItem === createLink('customer-management')
                  ? activeLinkRef
                  : undefined
              }
              component={Link}
              to={createLink('customer-management')}
              onClick={() => handleClick(createLink('customer-management'))}
              isActive={activeItem === createLink('customer-management')}
            >
              <SidebarIcon
                isActive={activeItem === createLink('customer-management')}
              >
                <CustomerIcon
                  color={
                    activeItem === createLink('customer-management')
                      ? 'var(--bg-color)'
                      : 'var(--subtext)'
                  }
                  size="16px"
                />
              </SidebarIcon>
              <SidebarLinkText
                isActive={activeItem === createLink('customer-management')}
              >
                Customers
              </SidebarLinkText>
            </SidebarLink>

            <SidebarLink
              ref={
                activeItem === createLink('vendor-management')
                  ? activeLinkRef
                  : undefined
              }
              component={Link}
              to={createLink('vendor-management')}
              onClick={() => handleClick(createLink('vendor-management'))}
              isActive={activeItem === createLink('vendor-management')}
            >
              <SidebarIcon
                component="i"
                className="bx bxs-store"
                isActive={activeItem === createLink('vendor-management')}
              />
              <SidebarLinkText
                isActive={activeItem === createLink('vendor-management')}
              >
                Vendors
              </SidebarLinkText>
            </SidebarLink>

            {!isExecutive && (
              <SidebarLink
                ref={
                  activeItem === createLink('production-management')
                    ? activeLinkRef
                    : undefined
                }
                component={Link}
                to={createLink('production-management')}
                onClick={() => handleClick(createLink('production-management'))}
                isActive={activeItem === createLink('production-management')}
              >
                <SidebarIcon
                  isActive={activeItem === createLink('production-management')}
                >
                  <ProductionIcon
                    color={
                      activeItem === createLink('production-management')
                        ? 'var(--bg-color)'
                        : 'var(--subtext)'
                    }
                    size="16px"
                  />
                </SidebarIcon>
                <SidebarLinkText
                  isActive={activeItem === createLink('production-management')}
                >
                  Productions
                </SidebarLinkText>
              </SidebarLink>
            )}

            {isAdminOrOwner && (
              <>
                <SidebarDivider />

                <SidebarDropdownToggle
                  isOpen={reportsDropdownOpen}
                  onClick={() => setReportsDropdownOpen(!reportsDropdownOpen)}
                >
                  <SidebarDropdownToggleText>Reports</SidebarDropdownToggleText>
                  <SidebarDropdownArrow
                    component="i"
                    className="bx bx-chevron-down"
                    isOpen={reportsDropdownOpen}
                  />
                </SidebarDropdownToggle>

                <SidebarDropdownContent isOpen={reportsDropdownOpen}>
                  <SidebarLink
                    ref={
                      activeItem === createLink('order-report')
                        ? activeLinkRef
                        : undefined
                    }
                    component={Link}
                    to={createLink('order-report')}
                    onClick={() => handleClick(createLink('order-report'))}
                    isActive={activeItem === createLink('order-report')}
                  >
                    <SidebarIcon
                      component="i"
                      className="bx bxs-detail"
                      isActive={activeItem === createLink('order-report')}
                    />
                    <SidebarLinkText
                      isActive={activeItem === createLink('order-report')}
                    >
                      Order Report
                    </SidebarLinkText>
                  </SidebarLink>
                  <SidebarLink
                    ref={
                      activeItem === createLink('product-report')
                        ? activeLinkRef
                        : undefined
                    }
                    component={Link}
                    to={createLink('product-report')}
                    onClick={() => handleClick(createLink('product-report'))}
                    isActive={activeItem === createLink('product-report')}
                  >
                    <SidebarIcon
                      component="i"
                      className="bx bxs-bar-chart-alt-2"
                      isActive={activeItem === createLink('product-report')}
                    />
                    <SidebarLinkText
                      isActive={activeItem === createLink('product-report')}
                    >
                      Product Report
                    </SidebarLinkText>
                  </SidebarLink>
                  <SidebarLink
                    ref={
                      activeItem === createLink('payment-report')
                        ? activeLinkRef
                        : undefined
                    }
                    component={Link}
                    to={createLink('payment-report')}
                    onClick={() => handleClick(createLink('payment-report'))}
                    isActive={activeItem === createLink('payment-report')}
                  >
                    <SidebarIcon
                      component="i"
                      className="bx bx-money"
                      isActive={activeItem === createLink('payment-report')}
                    />
                    <SidebarLinkText
                      isActive={activeItem === createLink('payment-report')}
                    >
                      Payment Report
                    </SidebarLinkText>
                  </SidebarLink>
                  <SidebarLink
                    ref={
                      activeItem === createLink('customer-report')
                        ? activeLinkRef
                        : undefined
                    }
                    component={Link}
                    to={createLink('customer-report')}
                    onClick={() => handleClick(createLink('customer-report'))}
                    isActive={activeItem === createLink('customer-report')}
                  >
                    <SidebarIcon
                      component="i"
                      className="bx bxs-user-detail"
                      isActive={activeItem === createLink('customer-report')}
                    />
                    <SidebarLinkText
                      isActive={activeItem === createLink('customer-report')}
                    >
                      Customer Report
                    </SidebarLinkText>
                  </SidebarLink>
                  <SidebarLink
                    ref={
                      activeItem === createLink('customer-outstanding-report')
                        ? activeLinkRef
                        : undefined
                    }
                    component={Link}
                    to={createLink('customer-outstanding-report')}
                    onClick={() =>
                      handleClick(createLink('customer-outstanding-report'))
                    }
                    isActive={
                      activeItem === createLink('customer-outstanding-report')
                    }
                  >
                    <SidebarIcon
                      component="i"
                      className="bx bx-wallet"
                      isActive={
                        activeItem === createLink('customer-outstanding-report')
                      }
                    />
                    <SidebarLinkText
                      isActive={
                        activeItem === createLink('customer-outstanding-report')
                      }
                    >
                      Customer Outstanding Report
                    </SidebarLinkText>
                  </SidebarLink>
                </SidebarDropdownContent>
              </>
            )}

            {isAdminOrOwner && (
              <>
                <SidebarDivider />

                <SidebarDropdownToggle
                  isOpen={adminDropdownOpen}
                  onClick={() => setAdminDropdownOpen(!adminDropdownOpen)}
                >
                  <SidebarDropdownToggleText>
                    Administration
                  </SidebarDropdownToggleText>
                  <SidebarDropdownArrow
                    component="i"
                    className="bx bx-chevron-down"
                    isOpen={adminDropdownOpen}
                  />
                </SidebarDropdownToggle>

                <SidebarDropdownContent isOpen={adminDropdownOpen}>
                  <SidebarLink
                    ref={
                      activeItem === createLink('user-management')
                        ? activeLinkRef
                        : undefined
                    }
                    component={Link}
                    to={createLink('user-management')}
                    onClick={() => handleClick(createLink('user-management'))}
                    isActive={activeItem === createLink('user-management')}
                  >
                    <SidebarIcon
                      component="i"
                      className="bx bxs-user"
                      isActive={activeItem === createLink('user-management')}
                    />
                    <SidebarLinkText
                      isActive={activeItem === createLink('user-management')}
                    >
                      Users
                    </SidebarLinkText>
                  </SidebarLink>

                  <SidebarLink
                    ref={
                      activeItem === createLink('raw-material-management')
                        ? activeLinkRef
                        : undefined
                    }
                    component={Link}
                    to={createLink('raw-material-management')}
                    onClick={() =>
                      handleClick(createLink('raw-material-management'))
                    }
                    isActive={
                      activeItem === createLink('raw-material-management')
                    }
                  >
                    <SidebarIcon
                      component="i"
                      className="bx bxs-layer"
                      isActive={
                        activeItem === createLink('raw-material-management')
                      }
                    />
                    <SidebarLinkText
                      isActive={
                        activeItem === createLink('raw-material-management')
                      }
                    >
                      Raw Materials
                    </SidebarLinkText>
                  </SidebarLink>

                  <SidebarLink
                    ref={
                      activeItem ===
                      createLink('raw-material-category-management')
                        ? activeLinkRef
                        : undefined
                    }
                    component={Link}
                    to={createLink('raw-material-category-management')}
                    onClick={() =>
                      handleClick(
                        createLink('raw-material-category-management')
                      )
                    }
                    isActive={
                      activeItem ===
                      createLink('raw-material-category-management')
                    }
                  >
                    <SidebarIcon
                      component="i"
                      className="bx bxs-category"
                      isActive={
                        activeItem ===
                        createLink('raw-material-category-management')
                      }
                    />
                    <SidebarLinkText
                      isActive={
                        activeItem ===
                        createLink('raw-material-category-management')
                      }
                    >
                      Raw Material Categories
                    </SidebarLinkText>
                  </SidebarLink>

                  <SidebarLink
                    ref={
                      activeItem === createLink('product-management')
                        ? activeLinkRef
                        : undefined
                    }
                    component={Link}
                    to={createLink('product-management')}
                    onClick={() =>
                      handleClick(createLink('product-management'))
                    }
                    isActive={activeItem === createLink('product-management')}
                  >
                    <SidebarIcon
                      component="i"
                      className="bx bxs-package"
                      isActive={activeItem === createLink('product-management')}
                    />
                    <SidebarLinkText
                      isActive={activeItem === createLink('product-management')}
                    >
                      Products
                    </SidebarLinkText>
                  </SidebarLink>

                  <SidebarLink
                    ref={
                      activeItem === createLink('discount-management')
                        ? activeLinkRef
                        : undefined
                    }
                    component={Link}
                    to={createLink('discount-management')}
                    onClick={() =>
                      handleClick(createLink('discount-management'))
                    }
                    isActive={activeItem === createLink('discount-management')}
                  >
                    <SidebarIcon
                      isActive={
                        activeItem === createLink('discount-management')
                      }
                    >
                      <DiscountIcon
                        color={
                          activeItem === createLink('discount-management')
                            ? 'var(--bg-color)'
                            : 'var(--subtext)'
                        }
                        size="16px"
                      />
                    </SidebarIcon>
                    <SidebarLinkText
                      isActive={
                        activeItem === createLink('discount-management')
                      }
                    >
                      Discounts
                    </SidebarLinkText>
                  </SidebarLink>

                  <SidebarLink
                    ref={
                      activeItem === createLink('surcharges-management')
                        ? activeLinkRef
                        : undefined
                    }
                    component={Link}
                    to={createLink('surcharges-management')}
                    onClick={() =>
                      handleClick(createLink('surcharges-management'))
                    }
                    isActive={
                      activeItem === createLink('surcharges-management')
                    }
                  >
                    <SidebarIcon
                      component="i"
                      className="bx bxs-credit-card-front"
                      isActive={
                        activeItem === createLink('surcharges-management')
                      }
                    />
                    <SidebarLinkText
                      isActive={
                        activeItem === createLink('surcharges-management')
                      }
                    >
                      Surcharges
                    </SidebarLinkText>
                  </SidebarLink>

                  <SidebarLink
                    ref={
                      activeItem === createLink('bank-management')
                        ? activeLinkRef
                        : undefined
                    }
                    component={Link}
                    to={createLink('bank-management')}
                    onClick={() => handleClick(createLink('bank-management'))}
                    isActive={activeItem === createLink('bank-management')}
                  >
                    <SidebarIcon
                      component="i"
                      className="bx bxs-bank"
                      isActive={activeItem === createLink('bank-management')}
                    />
                    <SidebarLinkText
                      isActive={activeItem === createLink('bank-management')}
                    >
                      Bank Accounts
                    </SidebarLinkText>
                  </SidebarLink>

                  <SidebarLink
                    ref={
                      activeItem === createLink('voucher-type-management')
                        ? activeLinkRef
                        : undefined
                    }
                    component={Link}
                    to={createLink('voucher-type-management')}
                    onClick={() =>
                      handleClick(createLink('voucher-type-management'))
                    }
                    isActive={
                      activeItem === createLink('voucher-type-management')
                    }
                  >
                    <SidebarIcon
                      component="i"
                      className="bx bxs-coupon"
                      isActive={
                        activeItem === createLink('voucher-type-management')
                      }
                    />
                    <SidebarLinkText
                      isActive={
                        activeItem === createLink('voucher-type-management')
                      }
                    >
                      Voucher Types
                    </SidebarLinkText>
                  </SidebarLink>

                  <SidebarLink
                    ref={
                      activeItem === createLink('cheque-status')
                        ? activeLinkRef
                        : undefined
                    }
                    component={Link}
                    to={createLink('cheque-status')}
                    onClick={() => handleClick(createLink('cheque-status'))}
                    isActive={activeItem === createLink('cheque-status')}
                  >
                    <SidebarIcon
                      component="i"
                      className="bx bxs-check-circle"
                      isActive={activeItem === createLink('cheque-status')}
                    />
                    <SidebarLinkText
                      isActive={activeItem === createLink('cheque-status')}
                    >
                      Cheque Status
                    </SidebarLinkText>
                  </SidebarLink>

                  <SidebarLink
                    ref={
                      activeItem === createLink('production-mapping')
                        ? activeLinkRef
                        : undefined
                    }
                    component={Link}
                    to={createLink('production-mapping')}
                    onClick={() =>
                      handleClick(createLink('production-mapping'))
                    }
                    isActive={activeItem === createLink('production-mapping')}
                  >
                    <SidebarIcon
                      isActive={activeItem === createLink('production-mapping')}
                    >
                      <ProductionMappingIcon
                        color={
                          activeItem === createLink('production-mapping')
                            ? 'var(--bg-color)'
                            : 'var(--subtext)'
                        }
                        size="16px"
                      />
                    </SidebarIcon>
                    <SidebarLinkText
                      isActive={activeItem === createLink('production-mapping')}
                    >
                      Production mapping
                    </SidebarLinkText>
                  </SidebarLink>
                </SidebarDropdownContent>
              </>
            )}
          </SidebarContentPadding>
        </Scrollbar>
      </SidebarScrollableContent>

      <SidebarFooter>
        <SidebarDivider />

        <SidebarLink
          component={Link}
          to={createLink('settings')}
          onClick={() => handleClick(createLink('settings'))}
          isActive={activeItem === createLink('settings')}
        >
          <SidebarIcon
            component="i"
            className="bx bx-cog"
            isActive={activeItem === createLink('settings')}
          />
          <SidebarLinkText isActive={activeItem === createLink('settings')}>
            Settings
          </SidebarLinkText>
        </SidebarLink>

        <SidebarLink
          onClick={() => handleLogout(navigate, showSnackbar)}
          isActive={activeItem === 'logout'}
        >
          <SidebarIcon
            component="i"
            className="bx bx-exit"
            isActive={activeItem === 'logout'}
          />
          <SidebarLinkText isActive={activeItem === 'logout'}>
            Logout
          </SidebarLinkText>
        </SidebarLink>
      </SidebarFooter>
    </SidebarContainer>
  );
};

export default Sidebar;
